import { useMsal } from '@azure/msal-react';
import { LogOut, MessageSquare, PanelLeft, PanelRight } from 'lucide-react';
import { Outlet, useMatch, useParams } from 'react-router-dom';

import { useUiStore } from '../state/ui-store.js';

import { AccountMenu } from './shell/account-menu.js';
import { AppLogo } from './shell/app-logo.js';
import { RepoSwitcher } from './shell/repo-switcher.js';
import { ShellNav } from './shell/shell-nav.js';
import { ToolbarButton } from './shell/toolbar-button.js';

export function AppShell(): JSX.Element {
  const { instance, accounts } = useMsal();
  const params = useParams();
  const repo = params.name ?? null;
  const isGraphRoute = useMatch('/repos/:name') !== null;

  const leftPanelOpen = useUiStore((s) => s.leftPanelOpen);
  const rightPanelOpen = useUiStore((s) => s.rightPanelOpen);
  const setLeftPanelOpen = useUiStore((s) => s.setLeftPanelOpen);
  const setRightPanelOpen = useUiStore((s) => s.setRightPanelOpen);
  const account = accounts[0];

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-void text-text-primary">
      <header className="flex items-center gap-4 border-b border-dashed border-border-subtle bg-deep/85 px-5 py-2.5 backdrop-blur-md">
        <AppLogo />
        <RepoSwitcher repo={repo} />
        <ShellNav repo={repo} />
        <span className="flex-1" />

        {isGraphRoute && (
          <div className="flex items-center gap-1.5">
            <ToolbarButton
              active={leftPanelOpen}
              onClick={() => setLeftPanelOpen(!leftPanelOpen)}
              icon={<PanelLeft className="h-4 w-4" />}
              label="Files"
              ariaPressed={leftPanelOpen}
            />
            <ToolbarButton
              active={rightPanelOpen}
              onClick={() => setRightPanelOpen(!rightPanelOpen)}
              icon={<MessageSquare className="h-4 w-4" />}
              label="CodeGraph AI"
              ariaPressed={rightPanelOpen}
              accent
            />
            <ToolbarButton
              onClick={() => setRightPanelOpen(!rightPanelOpen)}
              icon={<PanelRight className="h-4 w-4" />}
              label=""
              srLabel="Toggle right panel"
            />
          </div>
        )}

        {account && (
          <AccountMenu
            email={account.username}
            onSignOut={() => void instance.logoutRedirect()}
            icon={<LogOut className="h-3.5 w-3.5" />}
          />
        )}
      </header>

      <main
        className={
          isGraphRoute
            ? 'flex min-h-0 flex-1 flex-col overflow-hidden'
            : 'min-h-0 flex-1 overflow-auto scrollbar-thin'
        }
      >
        <Outlet />
      </main>
    </div>
  );
}
